import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputAdornment, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { addDoc, collection, getDocs, getFirestore, orderBy, query, serverTimestamp, where } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import firebaseApp from '../utils/firebaseConfig';
import { matchesSearch } from '../utils/search';
import { formatPhoneDisplay, phoneDigits } from '../utils/phone';
import { pickBestMember } from '../utils/members';
import { DIRECTION, TRANSFER_STATUS, daysSince, dealerLine, firearmLabel, fmtDate, formatPrice, printTransferForm, staffName } from '../utils/transfers';
import TransferForm from '../sections/@dashboard/transfers/TransferForm';
import { docFromForm, emptyForm, upsertDealer } from '../sections/@dashboard/transfers/transferData';

const db = getFirestore(firebaseApp);
const FILTERS = [
  { key: 'requested', label: 'Solicitudes online' },
  { key: 'open', label: 'En proceso' },
  { key: 'received', label: 'Por recoger' },
  { key: 'completed', label: 'Completadas' },
  { key: 'cancelled', label: 'Canceladas' },
  { key: 'all', label: 'Todas' },
];
const randomCode = (n = 6) => { const c = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; let r = ''; for (let i = 0; i < n; i += 1) r += c[Math.floor(Math.random() * c.length)]; return r; };

// Transferencias FFL a FFL (pedido Lola 2026-10-19): B&B envía un arma a otra
// armería o la recibe a nombre del cliente. Lista + "Nueva transferencia"
// (como layaways: cliente por teléfono, armas, armería FFL, costo, documento
// para firmar). Las solicitudes de la web entran como "Solicitud online".
export default function TransfersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('requested');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'ffl_transfers'), orderBy('createdAt', 'desc')));
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('transfers', e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { if (!loading && filter === 'requested' && !rows.some((r) => r.status === 'requested')) setFilter('open'); }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => ({
    requested: rows.filter((r) => r.status === 'requested').length,
    open: rows.filter((r) => ['pending', 'shipped'].includes(r.status)).length,
    received: rows.filter((r) => r.status === 'received').length,
  }), [rows]);
  const filtered = useMemo(() => rows.filter((r) => {
    if (filter === 'requested') return r.status === 'requested';
    if (filter === 'open') return ['pending', 'shipped'].includes(r.status);
    if (filter === 'received') return r.status === 'received';
    if (filter === 'completed') return r.status === 'completed';
    if (filter === 'cancelled') return r.status === 'cancelled';
    return true;
  }).filter((r) => !search.trim() || matchesSearch(r.searchKey || '', search)), [rows, filter, search]);
  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Helmet><title> Transferencias FFL | BBTC </title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" useFlexGap gap={1}>
          <div>
            <Typography variant="h4">Transferencias FFL a FFL</Typography>
            <Typography variant="body2" color="text.secondary">B&B envía o recibe armas de otras armerías a nombre del cliente. Solicitud pública: bbtc-dashboard.web.app/transfer</Typography>
          </div>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Iconify icon="eva:book-open-outline" />} onClick={() => navigate('/dashboard/ffl-dealers')}>Armerías FFL</Button>
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setCreateOpen(true)}>Nueva transferencia</Button>
          </Stack>
        </Stack>
        <Card sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <ToggleButtonGroup exclusive size="small" value={filter} onChange={(e, v) => { if (v) { setFilter(v); setPage(0); } }} sx={{ flexWrap: 'wrap' }}>
                {FILTERS.map((f) => <ToggleButton key={f.key} value={f.key}>{f.label}{counts[f.key] ? ` (${counts[f.key]})` : ''}</ToggleButton>)}
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" placeholder="Buscar: cliente, teléfono, serie, armería, tracking…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }} />
            </Grid>
          </Grid>
        </Card>
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ref.</TableCell><TableCell>Cliente</TableCell><TableCell>Tipo</TableCell><TableCell>Arma(s)</TableCell><TableCell>Armería FFL</TableCell><TableCell>Fee</TableCell><TableCell>Creada</TableCell><TableCell>Estado</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableSkeleton rows={5} columns={9} />}
                  {!loading && paged.length === 0 && <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No hay transferencias en este filtro.</Typography></TableCell></TableRow>}
                  {!loading && paged.map((t) => {
                    const st = TRANSFER_STATUS[t.status] || TRANSFER_STATUS.pending;
                    const ds = daysSince(t.createdAt);
                    return (
                      <TableRow hover key={t.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/transfers/details?id=${t.id}`)}>
                        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{t.code}</Typography></TableCell>
                        <TableCell><Typography variant="subtitle2">{t.customerName}</Typography><Typography variant="caption" color="text.secondary">{formatPhoneDisplay(t.customerPhone)}{t.source === 'online' ? ' · web' : t.source === 'app' ? ' · app' : ''}</Typography></TableCell>
                        <TableCell><Chip size="small" variant="outlined" color={DIRECTION[t.direction] ? DIRECTION[t.direction].color : 'default'} label={DIRECTION[t.direction] ? DIRECTION[t.direction].label : t.direction} /></TableCell>
                        <TableCell><Typography variant="body2">{(t.firearms || []).map(firearmLabel).join(', ') || '—'}</Typography><Typography variant="caption" sx={{ fontFamily: 'monospace' }} color="text.secondary">{(t.firearms || []).map((f) => f.serialNumber).filter(Boolean).join(', ')}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{dealerLine(t.dealer) || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2" color={t.feePaid ? 'success.main' : 'text.primary'}>{formatPrice(t.feeCents)}{t.feePaid ? ' ✓' : ''}</Typography></TableCell>
                        <TableCell>{fmtDate(t.createdAt)}{ds !== null && ['pending', 'received', 'requested'].includes(t.status) && <Typography variant="caption" display="block" color={ds > 120 ? 'error.main' : ds > 90 ? 'warning.main' : 'text.secondary'}>{ds} d{ds > 120 ? ' · +120 d (contrato)' : ''}</Typography>}</TableCell>
                        <TableCell><Chip size="small" color={st.color} label={st.label} />{t.trackingNumber && <Typography variant="caption" display="block" sx={{ fontFamily: 'monospace' }}>{t.carrier} {t.trackingNumber}</Typography>}</TableCell>
                        <TableCell align="right"><Iconify icon="eva:arrow-ios-forward-fill" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Por página" />
        </Card>
      </Container>
      <TransferCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => { fetchRows(); navigate(`/dashboard/transfers/details?id=${id}`); }} />
    </>
  );
}

// Diálogo "Nueva transferencia": cliente por TELÉFONO (regla del proyecto) +
// formulario compartido. Al guardar, imprime el documento para la firma.
function TransferCreateDialog({ open, onClose, onCreated }) {
  const [phone, setPhone] = useState('');
  const [lookup, setLookup] = useState('idle');
  const [member, setMember] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [license, setLicense] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  useEffect(() => { if (open) { setPhone(''); setLookup('idle'); setMember(null); setName(''); setEmail(''); setLicense(''); setForm(emptyForm()); setError(''); setCreated(null); } }, [open]);

  const doLookup = async () => {
    const d = phoneDigits(phone);
    if (d.length < 7) return;
    setLookup('searching');
    try {
      let snap = await getDocs(query(collection(db, 'members'), where('phone', '==', d)));
      if (snap.empty && d.length === 10) snap = await getDocs(query(collection(db, 'members'), where('phone', '==', `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`)));
      if (snap.empty) { setLookup('notfound'); setMember(null); return; }
      const m = pickBestMember(snap.docs, { phone: d });
      setMember(m); setName(m.fullName || ''); setEmail(m.email || ''); setLicense(m.licenseNumber || m.gunLicense || ''); setLookup('found');
    } catch (e) { setLookup('idle'); }
  };

  const save = async () => {
    if (!name.trim() || phoneDigits(phone).length < 7) { setError('Completa el cliente (nombre y teléfono)'); return; }
    if (!form.dealer.name.trim()) { setError('Falta la armería FFL'); return; }
    const data = docFromForm(form, { customerName: name, customerPhone: phoneDigits(phone), customerEmail: email });
    if (data.firearms.length === 0) { setError('Añade al menos un arma (marca o número de serie)'); return; }
    setSaving(true); setError('');
    try {
      const staff = staffName();
      const dealer = await upsertDealer(data.dealer, staff);
      const code = `T-${randomCode()}`;
      const docData = {
        ...data, dealer, code, status: 'pending', source: 'staff',
        memberId: member ? member.id : null, customerName: name.trim(), customerPhone: phoneDigits(phone), customerEmail: email.trim().toLowerCase(), licenseNumber: license.trim(),
        feePaid: false, feePaidAt: null, feeMethod: '', notes: '', shippedAt: null, receivedAt: null, completedAt: null, cancelledAt: null, signature: null,
        history: [{ at: new Date(), by: staff, text: 'Transferencia creada en tienda' }],
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(), createdBy: staff,
        searchKey: `${code} ${data.searchKey}`,
      };
      const ref = await addDoc(collection(db, 'ffl_transfers'), docData);
      setCreated({ id: ref.id, ...docData });
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  };

  if (created) {
    return (
      <Dialog open={open} onClose={() => { onCreated(created.id); onClose(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Transferencia creada · {created.code}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2 }}>Transferencia de <b>{created.customerName}</b> guardada ({DIRECTION[created.direction].label}).</Alert>
          <Typography variant="body2" color="text.secondary">Imprime el documento "Firearms License Request for Transfer" con los datos llenos para que el cliente lo firme. También está en la ficha.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { onCreated(created.id); onClose(); }}>Ir a la ficha</Button>
          <Button variant="contained" startIcon={<Iconify icon="eva:printer-outline" />} onClick={() => printTransferForm(created)}>Imprimir documento</Button>
        </DialogActions>
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Nueva transferencia FFL a FFL</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Teléfono *" value={phone} onChange={(e) => { setPhone(e.target.value); setLookup('idle'); }} onBlur={doLookup} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doLookup(); } }} InputProps={{ endAdornment: <InputAdornment position="end">{lookup === 'searching' ? <CircularProgress size={16} /> : <Button size="small" onClick={doLookup}>Buscar</Button>}</InputAdornment> }} helperText={lookup === 'found' ? 'Cuenta encontrada' : lookup === 'notfound' ? 'Sin cuenta (se guarda con los datos escritos)' : 'Busca la cuenta por teléfono'} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Nombre completo *" value={name} onChange={(e) => setName(e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Email (tracking y avisos)" value={email} onChange={(e) => setEmail(e.target.value)} /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth size="small" label="Lic. de armas" value={license} onChange={(e) => setLicense(e.target.value)} /></Grid>
          </Grid>
          <TransferForm value={form} onChange={setForm} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Guardando…' : 'Crear transferencia'}</Button>
      </DialogActions>
    </Dialog>
  );
}
